import React, {useState, useEffect} from 'react';
import {IonBadge, IonButton, IonCard, IonCardContent, IonItem, IonLabel, IonSelect, IonSelectOption, IonIcon, IonText} from '@ionic/react';
import './ProductCard.css';
import CurrencyPrefix from "../../../components/financials/currency-prefix";
import {Customer, Product} from "../../../models";
import {personOutline, calendarOutline, timeOutline} from "ionicons/icons";
import {Ages} from "../../../utils/ages";
import {
    resolveProfileAllocation,
    requiresProfile as allocationRequiresProfile,
    allowsGuest,
    resolveCtaLabel,
    resolveAllocationHint,
} from "../../../utils/productPresentation";

interface Props {
    customer?: Customer | null;
    product: Product;
    onAddToCart: (product: Product, learnerId: number | null) => void;
    currencyPrefix?: string;
}

const ProductCard: React.FC<Props> = ({ customer, product, onAddToCart, currencyPrefix }) => {
    const { name, priceDisplay, description, meta } = product;
    const company = customer?.company;
    const profiles = customer?.profiles || [];
    const [selectedLearnerId, setSelectedLearnerId] = useState<number | null>(null);
    const [isAdding, setIsAdding] = useState(false);

    const allocation = resolveProfileAllocation(product);
    const requiresProfile = allocationRequiresProfile(allocation);
    const guestAllowed = allowsGuest(allocation);

    // Guest storefront (no customer) + a product that requires a profile: the guest
    // has no profiles to assign, so the buy button would be permanently disabled with
    // no explanation. Offer a path to authenticate instead of a dead-end button.
    const guestRequiresLogin = !customer && requiresProfile;
    const allocationHint = resolveAllocationHint(product, allocation);
    const ctaLabel = resolveCtaLabel(product);

    // Selector visibility: required → only when choosing between >1 profiles
    // (single profile auto-selects); optional → whenever any profile exists so
    // the family can assign or leave it as a guest; none → never.
    const showLearnerSelect =
        allocation === 'required' ? profiles.length > 1
        : allocation === 'optional' ? profiles.length >= 1
        : false;

    useEffect(() => {
        if (requiresProfile && profiles.length === 1) {
            setSelectedLearnerId(profiles[0].id);
        }
    }, [profiles, requiresProfile]);

    const handleBuy = () => {
        if (!requiresProfile || selectedLearnerId) {
            setIsAdding(true);
            onAddToCart(product, selectedLearnerId);
            setTimeout(() => {
                setIsAdding(false);
            }, 250);
        }
    };

    const formatEventDate = () => {
        if (!meta.eventStartDateTs) return undefined;
        const start = new Ages(meta.eventStartDateTs).toFormat('dd mmm yyyy');
        if (!meta.eventEndDateTs || meta.eventStartDateTs === meta.eventEndDateTs) return start;

        const end = new Ages(meta.eventEndDateTs).toFormat('dd mmm yyyy');
        if (start === end) return start;

        return `${start} – ${end}`;
    };

    const formatEventTime = () => {
        if (!meta.eventStartDateTs) return undefined;
        // If start and end are the same, only show date
        if (!meta.eventEndDateTs || meta.eventStartDateTs === meta.eventEndDateTs) return undefined;

        const startTime = new Ages(meta.eventStartDateTs).toFormat('hh:ii');
        const endTime = new Ages(meta.eventEndDateTs).toFormat('hh:ii');

        // If both are 00:00, it's likely just a date range without time
        if (startTime === '00:00' && endTime === '00:00') return undefined;

        return `${startTime} – ${endTime}`;
    };

    return (
        <IonCard className="product-card">
            <IonCardContent>
                <div className="product-card-top">
                    <div className="product-name">{name}</div>
                    <div className="product-price">
                        <CurrencyPrefix customer={customer} prefix={currencyPrefix} />
                        {priceDisplay}
                    </div>
                </div>

                {meta.eventName && (
                    <div className="product-package-name">
                        {meta.eventName}
                    </div>
                )}

                {product.startLabel && (
                    <div className="product-start-label">{product.startLabel}</div>
                )}

                {meta.isEventTicket && (
                    <div className="product-event-meta">
                        {formatEventDate() && (
                            <div className="event-meta-row">
                                <IonIcon icon={calendarOutline} />
                                <IonText color="medium">
                                    <p>
                                        {formatEventDate()}
                                        {formatEventTime() && `, ${formatEventTime()}`}
                                    </p>
                                </IonText>
                            </div>
                        )}
                        {meta.slots && meta.slots.length > 1 && (
                            <div className="product-slots-container">
                                {meta.slots.map((slot, index) => (
                                    <div key={index} className="event-meta-row">
                                        <IonIcon icon={timeOutline} />
                                        <IonText color="medium">
                                            <p>{slot.displayTime}</p>
                                        </IonText>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {(meta.eventDescription || description) && (
                    <div className="product-description">
                        {meta.eventDescription || description}
                    </div>
                )}

                <div className="product-meta">
                    {product.type !== 'event_package' && meta.lessonLimit && (
                        <IonBadge color="light" className="meta-badge">
                            {meta.lessonLimit} {meta.lessonLimit === 1 ? (company?.langLesson || 'session') : (company?.langLessons || 'sessions')}
                        </IonBadge>
                    )}
                    {product.type !== 'event_package' && meta.bookingLimit && (
                        <IonBadge color="light" className="meta-badge">
                            {meta.bookingLimit} {meta.bookingLimit === 1 ? 'booking' : 'bookings'}/week
                        </IonBadge>
                    )}
                    {product.type !== 'event_package' && !!meta.monthlyBookingLimit && (
                        <IonBadge color="light" className="meta-badge">
                            {meta.monthlyBookingLimit} {company?.langLessons || 'sessions'}/month
                        </IonBadge>
                    )}
                </div>

                {showLearnerSelect && (
                    <div className="product-learner-select">
                        <IonItem lines="none" className="ion-no-padding">
                            <IonIcon icon={personOutline} slot="start" size="small" />
                            <IonLabel position="stacked">Who is this for?</IonLabel>
                            <IonSelect
                                interface="popover"
                                value={selectedLearnerId}
                                placeholder={guestAllowed ? "Guest (Optional)" : "Select Profile"}
                                onIonChange={(e) => setSelectedLearnerId(e.detail.value)}
                            >
                                {guestAllowed && (
                                    <IonSelectOption value={null}>Guest / No Profile</IonSelectOption>
                                )}
                                {profiles.map((profile) => (
                                    <IonSelectOption key={profile.id} value={profile.id}>
                                        {profile.firstName} {profile.lastName}
                                    </IonSelectOption>
                                ))}
                            </IonSelect>
                        </IonItem>
                        {allocationHint && (
                            <IonText color="medium" className="allocation-hint">
                                <p>{allocationHint}</p>
                            </IonText>
                        )}
                    </div>
                )}

                <div className="product-card-bottom">
                    {guestRequiresLogin ? (
                        <>
                            <IonButton
                                expand="block"
                                routerLink="/auth/login"
                                className="login-cta"
                                data-testid="guest-login-cta"
                            >
                                Log in to purchase
                            </IonButton>
                            <IonText color="medium" className="allocation-hint">
                                <p>Log in or create an account to purchase.</p>
                            </IonText>
                        </>
                    ) : (
                        <IonButton
                            expand="block"
                            onClick={handleBuy}
                            className="buy-button"
                            disabled={(requiresProfile && !selectedLearnerId) || isAdding}
                        >
                            {ctaLabel}
                        </IonButton>
                    )}
                </div>
            </IonCardContent>
        </IonCard>
    );
};

export default ProductCard;
